import type { TFunction } from 'i18next';
import {
  chartButtonI18nKeyForStreamKey,
  isElectronicSensorStreamSensorKey,
  isMotorStreamSensorKey,
  motorChartButtonI18nKey,
  streamMetricSlug,
  streamPortFromSensorKey,
} from './sensor-utils';

export type ChartSeriesLabelOptions = {
  /** Stream parameters registered via `register_callable` in project code. */
  customCallableParameters?: ReadonlySet<string>;
  /** Non-localized motor variable names keyed by drive-unit port (from `buildMotorVariableNameByPort`). */
  motorVariableNameByPort?: ReadonlyMap<number, string>;
  /** Framework class names keyed by drive-unit port (from `buildMotorClassNameByPort`). */
  motorClassNameByPort?: ReadonlyMap<number, string>;
  /** Framework mechanism class names keyed by drive-unit port (from `buildMechanismClassNameByPort`). */
  mechanismClassNameByPort?: ReadonlyMap<number, string>;
  /** Non-localized sensor variable names keyed by drive-unit port (from `buildSensorVariableNameByPort`). */
  sensorVariableNameByPort?: ReadonlyMap<number, string>;
  /**
   * When true, motor series labels use metric names (`motor_multi` widget on one port).
   * Also enables incompatible-metric multi-axis y-axis layout in `LineGraph`.
   */
  useMotorMetricSeriesLabels?: boolean;
};

export function chartSeriesButtonI18nKey(dataKey: string): string {
  return chartButtonI18nKeyForStreamKey(dataKey);
}

function variableNameForSensorKey(dataKey: string, byPort: ReadonlyMap<number, string> | undefined): string | null {
  if (!byPort) return null;
  const port = streamPortFromSensorKey(dataKey);
  if (port === null) return null;
  return byPort.get(port) ?? null;
}

/** Display name for a chart series — matches the series toggle buttons. */
export function chartSeriesLabel(t: TFunction, dataKey: string, options?: ChartSeriesLabelOptions): string {
  if (options?.customCallableParameters?.has(dataKey)) return dataKey;

  if (options?.useMotorMetricSeriesLabels && isMotorStreamSensorKey(dataKey)) {
    const metric = streamMetricSlug(dataKey);
    return t(motorChartButtonI18nKey(metric), metric);
  }

  const motorName = variableNameForSensorKey(dataKey, options?.motorVariableNameByPort);
  if (motorName) return motorName;

  const sensorName = variableNameForSensorKey(dataKey, options?.sensorVariableNameByPort);
  if (sensorName) return sensorName;

  if (isMotorStreamSensorKey(dataKey) || isElectronicSensorStreamSensorKey(dataKey)) {
    return dataKey;
  }

  return t(chartSeriesButtonI18nKey(dataKey));
}

/** When true, series toggle buttons should render `chartSeriesLabel` as plain text (not `<Trans>`). */
export function chartSeriesUsesPlainLabel(dataKey: string, options?: ChartSeriesLabelOptions): boolean {
  if (options?.customCallableParameters?.has(dataKey)) return true;

  if (options?.useMotorMetricSeriesLabels && isMotorStreamSensorKey(dataKey)) {
    return false;
  }
  return (
    variableNameForSensorKey(dataKey, options?.motorVariableNameByPort) !== null ||
    variableNameForSensorKey(dataKey, options?.sensorVariableNameByPort) !== null
  );
}
